/**
 * fetchFailureLog.ts — how the entry point's fetch wrapper reports a request
 * that failed (2026-09-24).
 *
 * When a person leaves a page — a link, a reload, the address bar — the browser
 * cancels every request the old page still had in flight, and each one rejects
 * with the same `TypeError: Failed to fetch` that a real network failure
 * produces. Measured in Chromium: the rejection runs AFTER the page's
 * `pagehide` event (the old page's handlers still run while it unloads). The
 * wrapper in src/index.tsx logged every such rejection at console.error, so
 * each page change left one error line per cut-off request — nothing had
 * failed for anyone — and the live route walk (`a11y-console`) went red on six
 * of them.
 *
 * So: a failure while the page is leaving is logged as a warning; anything
 * else stays an error, byte for byte. The request is re-thrown either way —
 * only the log level depends on this. `pageshow` clears the flag, because a
 * page restored from the back/forward cache is a live page again.
 *
 * Firebase-free on purpose: src/index.tsx imports it before anything else runs.
 */

let leaving = false;
let installed = false;

/** Start watching for the page being left. Idempotent. */
export function installPageLeavingWatch(target: Pick<Window, 'addEventListener'> = window): void {
  if (installed) return;
  installed = true;
  target.addEventListener('pagehide', () => { leaving = true; });
  target.addEventListener('pageshow', () => { leaving = false; });
}

/** True between the page's `pagehide` and any later `pageshow`. */
export function isPageLeaving(): boolean {
  return leaving;
}

export type FetchFailureKind = 'api-proxy' | 'firebase-auth';

const PREFIX: Record<FetchFailureKind, string> = {
  'api-proxy': '[patchedFetch] API proxy call failed (re-throwing for caller retry):',
  'firebase-auth': '[patchedFetch] Firebase Auth call failed (re-throwing for SDK classification):',
};

/** Log a failed request at the level it deserves. Never throws. */
export function logFetchFailure(kind: FetchFailureKind, url: string, error: unknown): void {
  if (leaving) {
    console.warn(`${PREFIX[kind]} (cancelled — the page is closing)`, url, error);
    return;
  }
  console.error(PREFIX[kind], url, error);
}

/** Test-only: forget the watch so a suite can install it on a fresh target. */
export function __resetPageLeavingWatchForTests(): void {
  leaving = false;
  installed = false;
}
